import { observable } from "@legendapp/state";
import type { ChapterFeedV2 } from "../types";
import {
  lesson$,
  resetPlayerState,
  videoDuration$,
  activityVisible$,
  generatedActivities$,
  nextLesson,
  previousLesson,
} from "./videoState";

export const chapters$ = observable<ChapterFeedV2[]>([]);
export const feedCurrentIndex$ = observable(0);

/** Whether more feed pages may still be fetched (cleared once a page returns nothing new). */
export const feedHasMore$ = observable(true);
/** Whether a load-more fetch is currently in flight (so we never double-request). */
export const feedIsLoadingMore$ = observable(false);
/** Chapter IDs the user has watched/dropped — sent to the backend so it can skip them. */
export const skipChapterIDs$ = observable<number[]>([]);

/**
 * Remove a broken chapter from the feed and keep playback going (mirrors iOS
 * `handleVideoError` in FeedNew.tsx:1405-1428). The chapter is recorded in
 * `skipChapterIDs$` so a later load-more never re-serves it, then dropped from
 * the in-memory list; the current index is clamped so the next chapter slides up.
 */
export function dropBrokenChapter(chapterId: number): void {
  const chapters = chapters$.peek();
  const dropIdx = chapters.findIndex((c) => c.chapterID === chapterId);
  if (dropIdx === -1) return;
  if (!skipChapterIDs$.peek().includes(chapterId)) {
    skipChapterIDs$.set((ids) => [...ids, chapterId]);
  }
  const next = chapters.filter((c) => c.chapterID !== chapterId);
  chapters$.set(next);
  // Keep the current index pointing at the chapter that slid into this slot.
  const maxIdx = Math.max(0, next.length - 1);
  if (feedCurrentIndex$.peek() > maxIdx) feedCurrentIndex$.set(maxIdx);
  lesson$.currentLessonIndex.set(0);
  resetPlayerState();
  videoDuration$.set(0);
}

/**
 * Append newly-fetched chapters, de-duped against what we already have AND
 * against the skip list (iOS `loadMoreFeedItems` dedups by chapterID). Returns
 * how many genuinely-new chapters were added so the caller can clear
 * `feedHasMore$` when a page yields nothing new (the V2 feed serves-all then
 * loops, so dedup is what actually reaches an "end").
 */
export function appendChapters(incoming: ChapterFeedV2[]): number {
  const existing = chapters$.peek();
  const seen = new Set(existing.map((c) => c.chapterID));
  const skip = new Set(skipChapterIDs$.peek());
  const fresh = incoming.filter((c) => !seen.has(c.chapterID) && !skip.has(c.chapterID));
  if (fresh.length > 0) chapters$.set([...existing, ...fresh]);
  return fresh.length;
}

// ---------------------------------------------------------------------------
// Unified horizontal navigation
//
// The horizontal sequence within a chapter is:
//   video(l0) → [quiz(l0)] → video(l1) → [quiz(l1)] → … → next chapter
// where a quiz panel exists only when that lesson has an approved activity.
// Swipe, the < > arrows, and the activity card's "Next" button all drive the
// SAME stepForward/stepBackward so they can never disagree.
// ---------------------------------------------------------------------------

/**
 * Arrow / action-button taps don't mutate the index directly — that would jump
 * instantly. Instead they emit a directional scroll request that the carousel
 * picks up and turns into the SAME programmatic smooth scroll a swipe settles
 * through (HorizontalLessonCarousel). The carousel's scroll-settle path then
 * fires stepForward/stepBackward, so the visible animation is identical to a
 * swipe and the shared step state stays the single source of truth (no desync).
 * The payload is a monotonic counter (so repeated taps in the same direction
 * still register as new requests) plus the requested direction.
 */
export const feedScrollRequest$ = observable<{
  seq: number;
  direction: "forward" | "backward";
}>({ seq: 0, direction: "forward" });

export function requestStepForward(): void {
  feedScrollRequest$.set((r) => ({ seq: r.seq + 1, direction: "forward" }));
}

export function requestStepBackward(): void {
  feedScrollRequest$.set((r) => ({ seq: r.seq + 1, direction: "backward" }));
}

/**
 * Vertical (reel-to-reel) analog of feedScrollRequest$. The up/down nav buttons
 * push onto this; VerticalVideoCarousel turns it into the SAME smooth vertical
 * scroll a swipe-up/down settles through, and the scroll-settle path then calls
 * nextChapter()/previousChapter() — so the button animates exactly like a swipe
 * instead of the old instant index jump.
 */
export const feedVerticalScrollRequest$ = observable<{
  seq: number;
  direction: "next" | "previous";
}>({ seq: 0, direction: "next" });

export function requestNextChapter(): void {
  feedVerticalScrollRequest$.set((r) => ({ seq: r.seq + 1, direction: "next" }));
}

export function requestPreviousChapter(): void {
  feedVerticalScrollRequest$.set((r) => ({
    seq: r.seq + 1,
    direction: "previous",
  }));
}

function currentLessonHasQuiz(): boolean {
  const chapter = chapters$.peek()[feedCurrentIndex$.peek()];
  if (!chapter) return false;
  const lessonId = chapter.lessonIDs[lesson$.currentLessonIndex.peek()];
  return (generatedActivities$[lessonId]?.peek()?.length ?? 0) > 0;
}

/** Advance one step: video → its quiz (if any) → next video → … → next chapter. */
export function stepForward(): void {
  const chapter = chapters$.peek()[feedCurrentIndex$.peek()];
  if (!chapter) return;
  const idx = lesson$.currentLessonIndex.peek();
  const total = chapter.lessonIDs.length;
  if (currentLessonHasQuiz() && !activityVisible$.peek()) {
    activityVisible$.set(true); // video → quiz
  } else if (idx < total - 1) {
    nextLesson(total); // → next lesson's video (resetPlayerState clears activityVisible$)
  }
  // else: end of the chapter's horizontal sequence — stop here.
  // Moving between reels is vertical only (swipe up/down), never horizontal.
}

/** Go one step back: quiz → its video → previous video. Stays within the chapter. */
export function stepBackward(): void {
  if (activityVisible$.peek()) {
    activityVisible$.set(false); // quiz → video
    return;
  }
  if (lesson$.currentLessonIndex.peek() > 0) {
    previousLesson();
  }
}

/** Reactive (uses .get()) — for arrow disabled states in observer components. */
export function canStepForward(): boolean {
  const chapter = chapters$.get()[feedCurrentIndex$.get()];
  if (!chapter) return false;
  const idx = lesson$.currentLessonIndex.get();
  const lessonId = chapter.lessonIDs[idx];
  const hasQuiz = (generatedActivities$[lessonId]?.get()?.length ?? 0) > 0;
  const showingActivity = activityVisible$.get();
  // Horizontal forward stays within the chapter; the arrow greys out at the end.
  return (hasQuiz && !showingActivity) || idx < chapter.lessonIDs.length - 1;
}

export function canStepBackward(): boolean {
  return activityVisible$.get() || lesson$.currentLessonIndex.get() > 0;
}

export function nextChapter(): void {
  // When the feed is exhausted, allow advancing one slot PAST the last chapter
  // onto the end-of-content screen (virtual index === chapters.length).
  const lastChapterIdx = chapters$.get().length - 1;
  const max = feedHasMore$.get() ? lastChapterIdx : lastChapterIdx + 1;
  if (feedCurrentIndex$.get() >= max) return;
  feedCurrentIndex$.set((i) => Math.min(max, i + 1));
  lesson$.currentLessonIndex.set(0);
  resetPlayerState();
  videoDuration$.set(0);
}

export function previousChapter(): void {
  if (feedCurrentIndex$.get() <= 0) return;
  feedCurrentIndex$.set((i) => Math.max(0, i - 1));
  lesson$.currentLessonIndex.set(0);
  resetPlayerState();
  videoDuration$.set(0);
}

/**
 * Jump directly to the chapter with `chapterId`, mirroring iOS FeedNew.tsx:341-395:
 * the target chapter is removed from its old position (if loaded), re-inserted
 * right after the user's current position, and scrolled to. When the chapter
 * isn't in the loaded feed page, a feed item is synthesized from the liked
 * item's `lessonIds` (LikedChapterItem carries them) exactly like iOS's
 * targetChapter splice. Returns whether the jump happened.
 */
export function jumpToChapter(
  chapterId: number,
  liked?: { lessonIds: number[]; title?: string },
): boolean {
  const chapters = chapters$.peek();
  const currentIdx = Math.min(
    feedCurrentIndex$.peek(),
    Math.max(0, chapters.length - 1),
  );
  if (chapters[currentIdx]?.chapterID === chapterId) return true;
  const existing = chapters.find((c) => c.chapterID === chapterId);
  const item: ChapterFeedV2 | undefined =
    existing ??
    (liked && liked.lessonIds.length > 0
      ? {
          chapterID: chapterId,
          lessonIDs: liked.lessonIds,
          dialect: "unknown",
          title: liked.title ?? "",
          isLiked: true,
        }
      : undefined);
  if (!item) return false;
  // Remove from old position (avoids duplicates), insert at current + 1.
  const filtered = chapters.filter((c) => c.chapterID !== chapterId);
  const insertIdx = Math.min(currentIdx + 1, filtered.length);
  filtered.splice(insertIdx, 0, { ...item, isLiked: true });
  chapters$.set(filtered);
  feedCurrentIndex$.set(insertIdx);
  lesson$.currentLessonIndex.set(0);
  resetPlayerState();
  videoDuration$.set(0);
  return true;
}

/**
 * Reset the feed back to the top — chapter 0, lesson 0, fresh player state.
 * Used by the settings-driven refresh (iOS `resetFeed` → `clearFeedStore`,
 * FeedNew.tsx:265 / feedStore.ts:106-123, which resets the current feed index
 * and clears `chapterIDtoActiveLessonIdx`, the per-chapter active-lesson index).
 * The web tracks a single active-lesson index (`lesson$.currentLessonIndex`),
 * so resetting it to 0 is the web equivalent of clearing that map.
 */
export function resetFeedPosition(): void {
  feedCurrentIndex$.set(0);
  feedHasMore$.set(true);
  lesson$.currentLessonIndex.set(0);
  resetPlayerState();
  videoDuration$.set(0);
}
